import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { Container, Row, Col, Table, Badge, Dropdown } from "react-bootstrap";
import {
  getAllOrganisation,
  updateOrganisationStatus,
} from "store/actions/organizationSlice";
import CommonTitleBar from "components/common/CommonTitleBar";
import Paginations from "components/common/Paginations";
import NoDataFound from "components/common/NoDataFound";
import TableShimmer from "components/ShimmerEffects/CommonShimmer/TableShimmer";
import { useTranslation } from "react-i18next";
import { InputSwitch } from "primereact/inputswitch";
import { showAlert } from "store/reducers/alertReducer";
import { handleStopPropagation } from "components/common/Utils";
import { Ellipsis } from "lucide-react";
import Filters from "components/common/Filters";
import {
  ManagerStatusBadge,
  MerchantOnboardingBadge,
} from "components/common/OrganizationMerchantStatus";

const Organisation = () => {
  const { t } = useTranslation();
  document.title = t("Organisation | Work Space");

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { search } = useLocation();
  const pageNumber = Number(new URLSearchParams(search).get("page")) || 1;

  const {
    isLoading,
    loading,
    perPage,
    totalCount,
    totalPages,
    currentPage,
    organisations,
  } = useSelector((state) => state.Organizations);

  const [selectedId, setSelectedId] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [show, setShow] = useState(false); // ADD/EDIT MODAL
  const [showModal, setShowModal] = useState(false); // DELETE MODAL

  const [selectedFilters, setSelectedFilters] = useState({
    status: -1,
    orderBy: -1,
  });
  const [keyword, setKeyword] = useState("");

  const getAllData = (page, action) => {
    const { actionType } = action || {}; // SEARCH, RESET, RESET_KEYWORD
    const isReset = actionType === "RESET";

    const payload = {
      perPage,
      pageNo: page || pageNumber,
      status: selectedFilters.status,
      orderBy: selectedFilters.orderBy,
      keyword: ["RESET", "RESET_KEYWORD"].includes(actionType)
        ? ""
        : keyword || "",
    };
    dispatch(getAllOrganisation(payload));
    navigate(`/management/organisation?page=${page || pageNumber}`);
  };

  const handleReset = () => {
    setKeyword("");
    setSelectedFilters({ orderBy: -1, status: -1 });
    getAllData(pageNumber, { actionType: "RESET" });
  };

  const handleStatusChange = (id, currentStatus) => {
    setSelectedId(id);
    const payload = {
      id,
      status: currentStatus === 1 ? 0 : 1,
    };

    dispatch(updateOrganisationStatus(payload))
      .then((action) => {
        const { statusCode, success, message } = action?.payload;
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }));
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
        }
        setSelectedId(null);
      })
      .catch(() => {
        setSelectedId(null);
      });
  };

  const handleShow = () => setShow(true);

  useEffect(() => {
    getAllData(pageNumber);
  }, []);

  return (
    <div className="page-content organisation_page common_page">
      <Container fluid>
        <CommonTitleBar
          title={t("Organisation")}
          isCreateBtn={true}
          createBtnText={t("Add New")}
          onCreateBtnClick={() =>
            navigate("/management/organisation/invite-organisation")
          }
        />
        <div className="common_wrapper">
          <Filters
            keyword={keyword}
            setKeyword={setKeyword}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            onSearch={getAllData}
            onReset={handleReset}
            isDateRange={false}
          />
          <Row>
            <Col lg={12}>
              {loading ? (
                <TableShimmer col={8} />
              ) : organisations.length === 0 ? (
                <NoDataFound text={t("No organisation found")} />
              ) : (
                <div className="common_table_style">
                  <Table>
                    <thead className="t_head">
                      <tr>
                        <th>#</th>
                        <th>{t("Name")}</th>
                        <th>{t("Email")}</th>
                        <th>{t("Phone")}</th>
                        <th>{t("Status")}</th>
                        <th>{t("Manager Status")}</th>
                        <th>{t("Merchant Onboarding")}</th>
                        <th>{t("Actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {organisations.map((item, index) => (
                        <tr key={item._id}>
                          <td>{(currentPage - 1) * perPage + index + 1}</td>
                          <td>
                            <p
                              onClick={() =>
                                navigate(
                                  `/organisation/organisation-details?id=${item?._id}`,
                                )
                              }
                              className="c_p text-decoration-underline"
                            >
                              {item.name}
                            </p>
                          </td>
                          <td>{item.email}</td>
                          <td>{item.phone}</td>
                          <td>
                            <Badge
                              bg={
                                item.status === 1
                                  ? " bg_primary"
                                  : " bg-secondary"
                              }
                            >
                              {item.status === 1 ? t("Active") : t("Deactive")}
                            </Badge>
                          </td>
                          <td>
                            <ManagerStatusBadge status={item.managerStatus} />
                          </td>
                          <td>
                            <MerchantOnboardingBadge
                              merchantInfo={item.merchantInfo}
                            />
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-3">
                              <InputSwitch
                                checked={item.status === 1}
                                disabled={isLoading && selectedId === item._id}
                                onChange={() =>
                                  handleStatusChange(item._id, item.status)
                                }
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Col>
          </Row>
        </div>

        {!loading && (
          <Paginations
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => getAllData(page)}
            totalCount={totalCount}
          />
        )}
      </Container>
    </div>
  );
};

export default Organisation;
